import React, { useContext, useMemo, useState } from "react";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import {
  Button,
  Modal,
  Row,
  Col,
  ProgressBar,
  InputGroup,
} from "react-bootstrap";
import { useDispatch } from "react-redux";
import { createWebhook } from "../../redux/slices/webhook";
import NotyfContext from "../../contexts/NotyfContext";
import { useWhitelabel } from "../../contexts/WhitelabelContext";

/* -------------------- Validation -------------------- */
const validationSchema = Yup.object().shape({
  name: Yup.string().required("Required"),
  targetEntity: Yup.string().required("Required"),
  triggerAction: Yup.string().required("Required"),

  enableTriggerDelay: Yup.boolean(),

  interval: Yup.object().shape({
    amount: Yup.number()
      .transform((val, orig) => (orig === "" || orig === null ? null : val))
      .nullable()
      .when("enableTriggerDelay", {
        is: true,
        then: (s) => s.required("Required"),
        otherwise: (s) => s.notRequired(),
      }),
    unit: Yup.string()
      .transform((val, orig) => (orig === "" ? null : val))
      .nullable()
      .when("enableTriggerDelay", {
        is: true,
        then: (s) => s.required("Required"),
        otherwise: (s) => s.notRequired(),
      }),
  }),

  url: Yup.string().url("Invalid URL").required("Required"),
  requestMethod: Yup.string().required("Required"),
  payloadFormat: Yup.string().required("Required"),

  requestHeaders: Yup.array()
    .of(
      Yup.object().shape({
        key: Yup.string().nullable(),
        value: Yup.string().nullable(),
      })
    )
    .notRequired(),

  requestBodies: Yup.array()
    .of(
      Yup.object().shape({
        key: Yup.string().nullable(),
        value: Yup.string().nullable(),
      })
    )
    .notRequired(),
});

/* -------------------- Helpers -------------------- */
const steps = [
  { key: "basics", title: "Basics" },
  { key: "delay", title: "Trigger Delay" },
  { key: "delivery", title: "Delivery & Payload" },
  { key: "review", title: "Summary" },
];

const stepFields = {
  0: ["name", "targetEntity", "triggerAction"],
  1: ["enableTriggerDelay", "interval.amount", "interval.unit"],
  2: [
    "url",
    "requestMethod",
    "payloadFormat",
    "requestHeaders",
    "requestBodies",
  ],
  3: [], // review step has no blocking fields
};

const initialValues = {
  name: "",
  targetEntity: "",
  triggerAction: "",
  enableTriggerDelay: false,
  interval: {
    amount: "",
    unit: "",
  },
  url: "",
  requestMethod: "",
  payloadFormat: "",
  requestHeaders: [],
  requestBodies: [],
};

/* -------------------- UI Subcomponents -------------------- */
const StepHeader = ({ current }) => {
  const { backgroundColor, fontColor, accentColor } = useWhitelabel();
  const percent = Math.round(((current + 1) / steps.length) * 100);
  return (
    <div className="mb-3">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="d-flex gap-2">
          {steps.map((s, idx) => (
            <div
              key={s.key}
              className="px-3 py-1 rounded-pill fw-medium"
              style={{
                backgroundColor:
                  idx === current ? accentColor : backgroundColor,
                color: fontColor,
              }}
              aria-current={idx === current ? "step" : undefined}
            >
              {idx + 1}. {s.title}
            </div>
          ))}
        </div>
        <div className="text-muted small">{percent}%</div>
      </div>
      <ProgressBar
        now={percent}
        style={{ height: "8px" }}
        className="rounded-pill"
      >
        <div
          className="progress-bar"
          role="progressbar"
          style={{
            width: `${percent}%`,
            backgroundColor: backgroundColor, // ← your custom color here
          }}
        />
      </ProgressBar>
    </div>
  );
};

const FieldError = ({ error, touched }) =>
  touched && error ? (
    <div className="text-danger small mt-1" role="alert">
      {error}
    </div>
  ) : null;

const KeyValueList = ({ name, values, errors, touched, label, addLabel }) => {
  const { backgroundColor, fontColor } = useWhitelabel();
  return (
    <FieldArray
      name={name}
      render={(arrayHelpers) => (
        <div>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <label className="form-label m-0">{label}</label>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => arrayHelpers.push({ key: "", value: "" })}
              style={{
                "--dynamic-bg-color": backgroundColor,
                "--dynamic-font-color": fontColor,
              }}
            >
              + {addLabel}
            </Button>
          </div>

          {values?.length === 0 && (
            <div className="text-muted small mb-2">
              No entries. Click “+ {addLabel}” to add one.
            </div>
          )}

          {values?.map((row, idx) => {
            const keyPath = `${name}[${idx}].key`;
            const valPath = `${name}[${idx}].value`;
            const rowError = errors?.[idx] || {};
            const rowTouched = touched?.[idx] || {};
            return (
              <Row key={idx} className="g-2 align-items-start mb-2">
                <Col md={5}>
                  <input
                    className={`form-control ${
                      rowTouched?.key && rowError?.key ? "is-invalid" : ""
                    }`}
                    placeholder="Key"
                    name={keyPath}
                    value={row.key ?? ""}
                    onChange={(e) => {
                      const next = [...values];
                      next[idx] = { ...next[idx], key: e.target.value };
                      arrayHelpers.form.setFieldValue(name, next);
                    }}
                  />
                  <FieldError error={rowError?.key} touched={rowTouched?.key} />
                </Col>
                <Col md={6}>
                  <input
                    className={`form-control ${
                      rowTouched?.value && rowError?.value ? "is-invalid" : ""
                    }`}
                    placeholder="Value"
                    name={valPath}
                    value={row.value ?? ""}
                    onChange={(e) => {
                      const next = [...values];
                      next[idx] = { ...next[idx], value: e.target.value };
                      arrayHelpers.form.setFieldValue(name, next);
                    }}
                  />
                  <FieldError
                    error={rowError?.value}
                    touched={rowTouched?.value}
                  />
                </Col>
                <Col md={1} className="d-flex">
                  <Button
                    variant="primary"
                    className="ms-auto"
                    onClick={() => arrayHelpers.remove(idx)}
                    aria-label={`Remove ${label} row ${idx + 1}`}
                    style={{
                      "--dynamic-bg-color": backgroundColor,
                      "--dynamic-font-color": fontColor,
                    }}
                  >
                    &times;
                  </Button>
                </Col>
              </Row>
            );
          })}
        </div>
      )}
    />
  );
};

/* -------------------- Main Component -------------------- */
const NewWebhook = ({ showNewWebhook, setShowNewWebhook }) => {
  const dispatch = useDispatch();
  const notyf = useContext(NotyfContext);
  const [step, setStep] = useState(0);

  const nextStep = () => setStep((s) => Math.min(steps.length - 1, s + 1));
  const prevStep = () => setStep((s) => Math.max(0, s - 1));
  const isLast = step === steps.length - 1;
  const isFirst = step === 0;
  const { backgroundColor, fontColor } = useWhitelabel();

  const gateForStep = async (validateForm, values) => {
    // Validate only fields relevant to current step
    const errors = await validateForm(values);
    const relevant = stepFields[step];
    if (!relevant.length) return true;
    const hasErr = relevant.some((path) => {
      // check nested error existence by path
      const parts = path.split(".");
      let cur = errors;
      for (const p of parts) {
        if (!cur) break;
        cur = cur[p];
      }
      return Boolean(cur);
    });
    return !hasErr;
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const res = await dispatch(createWebhook(values));
      // If your slice returns a truthy “error” on failure, adjust accordingly:
      if (res?.error) {
        throw new Error(res.error?.message || "Failed to create webhook");
      }
      notyf.success("Webhook created");
      resetForm();
      setShowNewWebhook(false);
    } catch (e) {
      notyf.error(e.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={showNewWebhook}
      onHide={() => setShowNewWebhook(false)}
      centered
      backdrop="static"
      scrollable
      size="lg"
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnBlur
        validateOnChange
      >
        {({
          values,
          errors,
          touched,
          isSubmitting,
          handleChange,
          handleBlur,
          handleSubmit,
          validateForm,
          setFieldValue,
        }) => {
          const canShowIntervalFields = values.enableTriggerDelay;

          return (
            <Form onSubmit={handleSubmit} noValidate>
              <Modal.Header closeButton>
                <Modal.Title>New Webhook</Modal.Title>
              </Modal.Header>

              <Modal.Body>
                <StepHeader current={step} />

                {/* ------------ Step 1: Basics ------------ */}
                {step === 0 && (
                  <div className="mt-3">
                    <div className="mb-3">
                      <label htmlFor="name" className="form-label">
                        Name
                      </label>
                      <input
                        id="name"
                        name="name"
                        className={`form-control ${
                          touched.name && errors.name ? "is-invalid" : ""
                        }`}
                        placeholder="Human-friendly identifier (e.g., 'CRM Lead Created')"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        autoFocus
                      />
                      <FieldError error={errors.name} touched={touched.name} />
                    </div>

                    <Row className="g-3">
                      <Col md={6}>
                        <label htmlFor="targetEntity" className="form-label">
                          Target Entity
                        </label>
                        <select
                          id="targetEntity"
                          name="targetEntity"
                          className={`form-select ${
                            touched.targetEntity && errors.targetEntity
                              ? "is-invalid"
                              : ""
                          }`}
                          value={values.targetEntity}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        >
                          <option value="">Select entity…</option>
                          <option value="SIM">SIM</option>
                          <option value="INVOICE">Invoice</option>
                          <option value="WATCHDOG">Watchdog</option>
                          {/* TODO: insert your options here */}
                          {/* <option value="order">Order</option> */}
                        </select>
                        <FieldError
                          error={errors.targetEntity}
                          touched={touched.targetEntity}
                        />
                      </Col>

                      <Col md={6}>
                        <label htmlFor="triggerAction" className="form-label">
                          Trigger Action
                        </label>
                        <select
                          id="triggerAction"
                          name="triggerAction"
                          className={`form-select ${
                            touched.triggerAction && errors.triggerAction
                              ? "is-invalid"
                              : ""
                          }`}
                          value={values.triggerAction}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        >
                          <option value="">Select action…</option>
                          {values.targetEntity === "SIM" && (
                            <>
                              <option value="ACTIVATE">Activate</option>
                              <option value="SUSPEND">Suspend</option>
                              <option value="RESUME">Resume</option>
                              <option value="TERMINATE">Terminate</option>
                            </>
                          )}
                          {values.targetEntity === "WATCHDOG" && (
                            <>
                              <option value="CREATE">Create</option>
                              <option value="UPDATE">Update</option>
                              <option value="DELETE">Delete</option>
                            </>
                          )}
                          {values.targetEntity === "INVOICE" && (
                            <>
                              <option value="CREATE">Create</option>
                              <option value="UPDATE">Update</option>
                              <option value="DELETE">Delete</option>
                            </>
                          )}
                          {/* TODO: insert your options here */}
                          {/* <option value="created">Created</option> */}
                        </select>
                        <FieldError
                          error={errors.triggerAction}
                          touched={touched.triggerAction}
                        />
                      </Col>
                    </Row>
                  </div>
                )}

                {/* ------------ Step 2: Trigger Delay ------------ */}
                {step === 1 && (
                  <div className="mt-3">
                    <div className="form-check form-switch mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="enableTriggerDelay"
                        name="enableTriggerDelay"
                        checked={values.enableTriggerDelay}
                        onChange={(e) =>
                          setFieldValue("enableTriggerDelay", e.target.checked)
                        }
                        onBlur={handleBlur}
                        style={{
                          "--dynamic-bg-color": backgroundColor,
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="enableTriggerDelay"
                      >
                        Enable trigger delay
                      </label>
                    </div>

                    <Row className="g-3">
                      <Col md={6}>
                        <label htmlFor="interval.amount" className="form-label">
                          Delay Amount
                        </label>
                        <InputGroup>
                          <input
                            id="interval.amount"
                            name="interval.amount"
                            type="number"
                            min="0"
                            className={`form-control ${
                              touched?.interval?.amount &&
                              errors?.interval?.amount
                                ? "is-invalid"
                                : ""
                            }`}
                            placeholder={
                              canShowIntervalFields ? "e.g., 5" : "—"
                            }
                            value={values.interval.amount}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={!canShowIntervalFields}
                          />
                        </InputGroup>
                        <FieldError
                          error={errors?.interval?.amount}
                          touched={touched?.interval?.amount}
                        />
                      </Col>

                      <Col md={6}>
                        <label htmlFor="interval.unit" className="form-label">
                          Delay Unit
                        </label>
                        <select
                          id="interval.unit"
                          name="interval.unit"
                          className={`form-select ${
                            touched?.interval?.unit && errors?.interval?.unit
                              ? "is-invalid"
                              : ""
                          }`}
                          value={values.interval.unit ?? ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={!canShowIntervalFields}
                        >
                          <option value="">
                            {canShowIntervalFields
                              ? "Select unit…"
                              : "Delay disabled"}
                          </option>
                          <option value="minutes">Minutes</option>
                          <option value="hours">Hours</option>
                          <option value="days">Days</option>
                        </select>
                        <FieldError
                          error={errors?.interval?.unit}
                          touched={touched?.interval?.unit}
                        />
                      </Col>
                    </Row>
                    <div className="form-text mt-2">
                      Useful to prevent immediate firing—for example, wait 10
                      minutes to allow edits before sending.
                    </div>
                  </div>
                )}

                {/* ------------ Step 3: Delivery & Payload ------------ */}
                {step === 2 && (
                  <div className="mt-3">
                    <div className="mb-3">
                      <label htmlFor="url" className="form-label">
                        Callback URL
                      </label>
                      <input
                        id="url"
                        name="url"
                        className={`form-control ${
                          touched.url && errors.url ? "is-invalid" : ""
                        }`}
                        placeholder="https://example.com/webhooks/ingest"
                        value={values.url}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      <FieldError error={errors.url} touched={touched.url} />
                    </div>

                    <Row className="g-3">
                      <Col md={4}>
                        <label htmlFor="requestMethod" className="form-label">
                          Method
                        </label>
                        <select
                          id="requestMethod"
                          name="requestMethod"
                          className={`form-select ${
                            touched.requestMethod && errors.requestMethod
                              ? "is-invalid"
                              : ""
                          }`}
                          value={values.requestMethod}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        >
                          <option value="">Select method…</option>
                          <option value="GET">GET</option>
                          <option value="POST">POST</option>
                          <option value="PUT">PUT</option>
                          <option value="PATCH">PATCH</option>
                          <option value="DELETE">DELETE</option>
                        </select>
                        <FieldError
                          error={errors.requestMethod}
                          touched={touched.requestMethod}
                        />
                      </Col>
                      <Col md={8}>
                        <label htmlFor="payloadFormat" className="form-label">
                          Payload Format
                        </label>
                        <select
                          id="payloadFormat"
                          name="payloadFormat"
                          className={`form-select ${
                            touched.payloadFormat && errors.payloadFormat
                              ? "is-invalid"
                              : ""
                          }`}
                          value={values.payloadFormat}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        >
                          <option value="">Select format…</option>
                          <option value="JSON">JSON</option>
                          <option value="FORM">Form</option>
                        </select>
                        <FieldError
                          error={errors.payloadFormat}
                          touched={touched.payloadFormat}
                        />
                      </Col>
                    </Row>

                    <hr className="my-4" />

                    <KeyValueList
                      name="requestHeaders"
                      values={values.requestHeaders}
                      errors={errors.requestHeaders}
                      touched={touched.requestHeaders}
                      label="Request Headers"
                      addLabel="Header"
                    />

                    <hr className="my-4" />

                    <KeyValueList
                      name="requestBodies"
                      values={values.requestBodies}
                      errors={errors.requestBodies}
                      touched={touched.requestBodies}
                      label="Request Body (Key/Value)"
                      addLabel="Body Field"
                    />

                    <div className="form-text mt-2">
                      For JSON/XML payloads, you can send raw bodies from your
                      backend. Here we expose a key/value helper that’s handy
                      for simple cases or form data.
                    </div>
                  </div>
                )}

                {/* ------------ Step 4: Summary ------------ */}
                {step === 3 && (
                  <div className="mt-3">
                    <div className="mb-3">
                      <h6 className="mb-2">Basics</h6>
                      <div className="border rounded p-3 bg-light">
                        <div>
                          <strong>Name:</strong> {values.name || "—"}
                        </div>
                        <div>
                          <strong>Target Entity:</strong>{" "}
                          {values.targetEntity || "—"}
                        </div>
                        <div>
                          <strong>Trigger Action:</strong>{" "}
                          {values.triggerAction || "—"}
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <h6 className="mb-2">Trigger Delay</h6>
                      <div className="border rounded p-3 bg-light">
                        <div>
                          <strong>Enabled:</strong>{" "}
                          {values.enableTriggerDelay ? "Yes" : "No"}
                        </div>
                        {values.enableTriggerDelay && (
                          <div>
                            <strong>Interval:</strong>{" "}
                            {values.interval.amount || "—"}{" "}
                            {values.interval.unit || ""}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mb-3">
                      <h6 className="mb-2">Delivery & Payload</h6>
                      <div className="border rounded p-3 bg-light">
                        <div>
                          <strong>URL:</strong> {values.url || "—"}
                        </div>
                        <div>
                          <strong>Method:</strong> {values.requestMethod || "—"}
                        </div>
                        <div>
                          <strong>Payload Format:</strong>{" "}
                          {values.payloadFormat || "—"}
                        </div>
                        <div className="mt-2">
                          <strong>Headers:</strong>
                          {values.requestHeaders?.length ? (
                            <ul className="mb-0">
                              {values.requestHeaders.map((h, i) => (
                                <li key={i}>
                                  {h.key || "—"}: {h.value || "—"}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <div>—</div>
                          )}
                        </div>
                        <div className="mt-2">
                          <strong>Body (Key/Value):</strong>
                          {values.requestBodies?.length ? (
                            <ul className="mb-0">
                              {values.requestBodies.map((b, i) => (
                                <li key={i}>
                                  {b.key || "—"}: {b.value || "—"}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <div>—</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </Modal.Body>

              <Modal.Footer className="d-flex justify-content-between">
                <div className="text-muted small">
                  {step < 3
                    ? `Step ${step + 1} of ${steps.length}`
                    : "Review and create"}
                </div>
                <div className="d-flex gap-2">
                  <Button
                    variant="primary"
                    onClick={
                      isFirst ? () => setShowNewWebhook(false) : prevStep
                    }
                    style={{
                      "--dynamic-bg-color": backgroundColor,
                      "--dynamic-font-color": fontColor,
                    }}
                  >
                    {isFirst ? "Cancel" : "Back"}
                  </Button>
                  {!isLast ? (
                    <Button
                      variant="primary"
                      onClick={async () => {
                        const ok = await gateForStep(validateForm, values);
                        if (ok) nextStep();
                      }}
                      style={{
                        "--dynamic-bg-color": backgroundColor,
                        "--dynamic-font-color": fontColor,
                      }}
                    >
                      Next
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      variant="success"
                      disabled={isSubmitting}
                      style={{
                        "--dynamic-bg-color": backgroundColor,
                        "--dynamic-font-color": fontColor,
                      }}
                    >
                      {isSubmitting ? "Creating…" : "Create Webhook"}
                    </Button>
                  )}
                </div>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default NewWebhook;
